import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEffectivePermissions } from '../../hooks/useEffectivePermissions';
import { useInputMode } from '../../hooks/useInputMode';
import { useMySignQueueCount } from '../../hooks/useMySignQueueCount';
import { useTheme } from '../../context/ThemeContext';
import { useAppVersion } from '../../hooks/useAppVersion';
import {
  LogOut, X, PanelLeftClose, PanelLeftOpen, ChevronDown, ChevronRight,
  LayoutDashboard, Sun, Moon, Monitor,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { resolveGroups, DASHBOARD, slug, activeHref, hrefsForItem } from '../../lib/nav';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../ui/dropdown-menu';

const LS_GROUPS_MOBILE = 'sidebar.groups.mobile';
const HOVER_OPEN_DELAY = 150;
const HOVER_CLOSE_DELAY = 120;

function loadGroupState(key) {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
function saveGroupState(key, state) { try { localStorage.setItem(key, JSON.stringify(state)); } catch {/* no-op */} }

const THEME_OPTIONS = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

function ThemeMenu({ rail = false }) {
  const { mode, resolved, setMode } = useTheme();
  const Active = (THEME_OPTIONS.find((o) => o.value === mode) || THEME_OPTIONS[2]).Icon;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn('flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-800 hover:text-slate-100 transition-colors',
            rail ? 'min-w-[44px] min-h-[44px]' : 'p-2')}
          data-testid="theme-menu-btn"
          title={`Theme: ${mode}${mode === 'system' ? ` (${resolved})` : ''}`}
          aria-label={`Change theme. Current: ${mode}`}
        >
          <Active className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side="top" className="w-40">
        {THEME_OPTIONS.map(({ value, label, Icon }) => (
          <DropdownMenuItem key={value} onClick={() => setMode(value)} data-testid={`theme-opt-${value}`}
            className={cn('gap-2', mode === value && 'font-semibold')}>
            <Icon className="w-4 h-4" /> {label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function Sidebar({ mobileOpen, onClose, mode, onToggleMode, forceDrawer = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const inputMode = useInputMode();
  const perms = useEffectivePermissions();
  const canEvaluations = perms?.can?.('evaluations', 'view') || false;
  const signQueueCount = useMySignQueueCount(canEvaluations);
  const appVersion = useAppVersion();

  const visibleGroups = resolveGroups({ role: user?.role, perms });

  const allExpanded = visibleGroups.reduce((acc, g) => ({ ...acc, [g.id]: true }), {});
  const [mobileGroupState, setMobileGroupState] = useState(() => loadGroupState(LS_GROUPS_MOBILE) || allExpanded);
  useEffect(() => { saveGroupState(LS_GROUPS_MOBILE, mobileGroupState); }, [mobileGroupState]);

  const prevPath = useRef(location.pathname);
  useEffect(() => {
    if (prevPath.current !== location.pathname && mobileOpen && onClose) onClose();
    prevPath.current = location.pathname;
  }, [location.pathname, mobileOpen, onClose]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const handleNavClick = () => { if (onClose) onClose(); };

  // The page's owner = the single longest-matching nav href. An item/group is
  // active only if it owns that href, so a short prefix like `/scheduling` can
  // never hijack `/scheduling/pay-rate-review` (which belongs to Finance).
  const ownerHref = activeHref(visibleGroups, location.pathname);

  const isItemActive = useCallback((item) => {
    if (!ownerHref) return false;
    return hrefsForItem(item).includes(ownerHref);
  }, [ownerHref]);

  const activeGroupId = (() => {
    for (const g of visibleGroups) if (g.items.some(isItemActive)) return g.id;
    return null;
  })();

  const isTouch = inputMode === 'touch' || forceDrawer;
  const isHoverRail = !isTouch && mode === 'hover' && !mobileOpen;
  const renderingMobileDrawer = mobileOpen;

  const [pinnedExpandedId, setPinnedExpandedId] = useState(activeGroupId);
  const [hoveredGroupId, setHoveredGroupId] = useState(null);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);

  const scheduleOpen = (gid) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    if (hoveredGroupId === gid) return;
    if (openTimer.current) clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => setHoveredGroupId(gid), HOVER_OPEN_DELAY);
  };
  const scheduleClose = () => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHoveredGroupId(null), HOVER_CLOSE_DELAY);
  };
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };

  useEffect(() => { if (!isHoverRail) setHoveredGroupId(null); }, [isHoverRail]);
  useEffect(() => { setHoveredGroupId(null); }, [location.pathname]);
  useEffect(() => { setPinnedExpandedId(activeGroupId); }, [activeGroupId]);

  const togglePinnedGroup = (gid) => setPinnedExpandedId((prev) => (prev === gid ? null : gid));
  const toggleMobileGroup = (gid) => setMobileGroupState({ ...mobileGroupState, [gid]: !mobileGroupState[gid] });

  // ── render helpers ──
  const navItemClasses = (active, narrow) => cn(
    'relative flex items-center gap-3 rounded-md transition-colors text-sm font-medium',
    narrow ? 'justify-center px-2 py-2.5' : 'px-3 py-2.5',
    active ? 'bg-slate-800 text-white' : 'text-slate-300 hover:bg-slate-800/60 hover:text-white'
  );

  const renderDashboard = (narrow = false) => (
    <NavLink to="/" end onClick={handleNavClick}
      className={({ isActive }) => navItemClasses(isActive, narrow)}
      data-testid="nav-dashboard" title={narrow ? 'Dashboard' : undefined}>
      <LayoutDashboard className="w-5 h-5 shrink-0" />
      {!narrow && <span className="truncate">Dashboard</span>}
    </NavLink>
  );

  const renderNavItem = (item, narrow = false) => {
    const active = isItemActive(item);
    return (
      <NavLink key={item.id} to={item.href} onClick={handleNavClick}
        className={navItemClasses(active, narrow)}
        data-testid={`nav-${slug(item.name)}`} title={narrow ? item.name : undefined}>
        <item.icon className="w-5 h-5 shrink-0" />
        {!narrow && <span className="truncate flex-1">{item.name}</span>}
        {item.id === 'evaluations' && signQueueCount > 0 && (
          <span className={cn('inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-red-600 text-white',
            narrow ? 'absolute top-1 right-1 min-w-[16px] h-4 px-1' : 'ml-auto min-w-[20px] h-5 px-1.5')}
            data-testid="sign-queue-badge"
            aria-label={`${signQueueCount} evaluation${signQueueCount === 1 ? '' : 's'} awaiting your signature`}>
            {signQueueCount > 99 ? '99+' : signQueueCount}
          </span>
        )}
      </NavLink>
    );
  };

  const GroupHeader = ({ group, isOpen, onToggle }) => {
    const isActiveGroup = group.id === activeGroupId;
    return (
      <button type="button" onClick={onToggle} aria-expanded={isOpen}
        data-testid={`group-${group.id}-toggle`}
        className={cn('w-full flex items-center justify-between px-3 py-2 rounded-md text-xs uppercase tracking-wider font-semibold',
          'text-slate-400 hover:text-slate-200 hover:bg-slate-800/40 transition-colors', isActiveGroup && 'text-slate-200')}
        title={isOpen ? 'Collapse section' : 'Expand section'}>
        <span className="flex items-center gap-2"><group.icon className="w-3.5 h-3.5 opacity-70" />{group.label}</span>
        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
      </button>
    );
  };

  const renderPinnedGroup = (group) => {
    const isOpen = pinnedExpandedId === group.id;
    return (
      <div key={group.id} className="space-y-0.5">
        <GroupHeader group={group} isOpen={isOpen} onToggle={() => togglePinnedGroup(group.id)} />
        {isOpen && <div className="space-y-0.5 pl-1">{group.items.map((it) => renderNavItem(it, false))}</div>}
      </div>
    );
  };

  const renderMobileGroup = (group) => {
    const isOpen = !!mobileGroupState[group.id];
    return (
      <div key={group.id} className="space-y-0.5">
        <GroupHeader group={group} isOpen={isOpen} onToggle={() => toggleMobileGroup(group.id)} />
        {isOpen && <div className="space-y-0.5 pl-1">{group.items.map((it) => renderNavItem(it, false))}</div>}
      </div>
    );
  };

  const renderRailGroup = (group) => {
    const isActiveGroup = group.id === activeGroupId;
    const isOpen = hoveredGroupId === group.id;
    return (
      <div key={group.id} className="relative"
        onMouseEnter={() => scheduleOpen(group.id)} onMouseLeave={scheduleClose}
        data-testid={`rail-group-${group.id}`}>
        <button type="button" aria-expanded={isOpen} aria-label={group.label}
          className={cn('w-full flex items-center justify-center rounded-md px-2 py-2.5 transition-colors',
            'text-slate-300 hover:bg-slate-800/60 hover:text-white',
            isActiveGroup && 'text-white bg-slate-800/40 border-l-2 border-emerald-400 rounded-l-none pl-[6px]')}>
          <group.icon className="w-5 h-5 shrink-0" />
        </button>
        {isOpen && (
          <div className="absolute left-full top-0 ml-2 w-56 z-50 rounded-md bg-slate-900 border border-slate-700 shadow-2xl p-2"
            onMouseEnter={cancelClose} onMouseLeave={scheduleClose} data-testid={`rail-flyout-${group.id}`}>
            <div className="px-2 py-1 mb-1 flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-slate-400 border-b border-slate-700/60">
              <group.icon className="w-3.5 h-3.5 opacity-70" />{group.label}
            </div>
            <div className="space-y-0.5 pt-1">{group.items.map((it) => renderNavItem(it, false))}</div>
          </div>
        )}
      </div>
    );
  };

  const railWidth = 'w-16';
  const fullWidth = 'w-64';
  const desktopWidth = isHoverRail ? railWidth : fullWidth;
  const showFull = !isHoverRail || renderingMobileDrawer;
  const { isDark } = useTheme();
  const initials = `${user?.first_name?.[0] || ''}${user?.last_name?.[0] || ''}`;
  const versionLabel = `v${appVersion.version || '—'}${appVersion.build ? ` · ${appVersion.build}` : ''}`;

  return (
    <>
      {mobileOpen && <div className="fixed inset-0 z-40 bg-black/50" onClick={onClose} data-testid="sidebar-overlay" />}

      <aside
        className={cn('theme-pin-light fixed inset-y-0 left-0 z-50 bg-slate-900 text-slate-100 flex flex-col transition-all duration-200 ease-in-out',
          isTouch ? (mobileOpen ? `translate-x-0 ${fullWidth}` : `-translate-x-full ${fullWidth}`) : `translate-x-0 ${desktopWidth}`)}
        data-testid="sidebar">
        {/* Logo / header */}
        <div className={cn('flex items-center bg-sidebar-band border-b border-sidebar-band-border shrink-0',
          showFull ? 'justify-between px-2 py-2' : 'justify-center px-1 py-2')}>
          {showFull ? (
            <div className="flex items-center flex-1 min-w-0">
              <img src={isDark ? '/tricorps360-horizontal-white.png' : '/tricorps360-horizontal.png'}
                alt="TriCorps 360" className="h-16 w-full object-contain" data-testid="sidebar-brand-logo" />
            </div>
          ) : (
            <img src={isDark ? '/tricorps360-stacked-white.png' : '/tricorps360-stacked.png'}
              alt="TriCorps 360" className="w-14 h-14 object-contain" data-testid="sidebar-brand-logo" />
          )}
          {mobileOpen && (
            <button type="button" onClick={onClose}
              className="flex items-center justify-center min-w-[44px] min-h-[44px] -mr-2 hover:bg-slate-800 rounded-md transition-colors"
              data-testid="sidebar-close-btn" aria-label="Close menu">
              <X className="w-6 h-6 text-slate-400" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className={cn('flex-1 min-h-0 py-3 overflow-y-auto scrollbar-thin', showFull ? 'px-2 space-y-3' : 'px-2 space-y-1')}>
          {showFull ? (
            <>
              {renderDashboard(false)}
              {visibleGroups.map(renderingMobileDrawer ? renderMobileGroup : renderPinnedGroup)}
            </>
          ) : (
            <>
              {renderDashboard(true)}
              {visibleGroups.map(renderRailGroup)}
            </>
          )}
        </nav>

        {/* Footer */}
        <div className={cn('border-t border-slate-800 shrink-0', showFull ? 'p-3 space-y-2' : 'p-2')}>
          {showFull ? (
            <>
              {/* Row 1: avatar · name · sign out */}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center shrink-0"
                  title={`${user?.first_name || ''} ${user?.last_name || ''}\n${user?.email || ''}`}>
                  <span className="text-xs font-medium">{initials}</span>
                </div>
                <div className="flex-1 min-w-0" title={`${user?.first_name || ''} ${user?.last_name || ''} · ${user?.email || ''}`}>
                  <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.first_name} {user?.last_name}</p>
                </div>
                <button type="button" onClick={handleLogout}
                  className="flex items-center justify-center min-w-[44px] min-h-[44px] hover:bg-slate-800 rounded-md transition-colors text-slate-300 hover:text-white"
                  data-testid="logout-btn" title="Sign out" aria-label="Sign out">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
              {/* Row 2: version · theme · collapse */}
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => { onClose?.(); navigate('/whats-new'); }}
                  className="flex-1 min-w-0 text-left text-[10px] text-slate-500 hover:text-slate-300 transition-colors truncate"
                  data-testid="app-version"
                  title={appVersion.released ? `Released ${appVersion.released}${appVersion.build ? ` · build ${appVersion.build}` : ''} — see what's new` : "See what's new"}>
                  {versionLabel}
                </button>
                <ThemeMenu />
                {!mobileOpen && (
                  <button onClick={onToggleMode}
                    className="p-2 hover:bg-slate-800 rounded-md transition-colors text-slate-400 hover:text-slate-200"
                    data-testid="sidebar-collapse-btn"
                    title={mode === 'pinned' ? 'Collapse sidebar' : 'Expand sidebar'}
                    aria-label={mode === 'pinned' ? 'Collapse sidebar' : 'Expand sidebar'}>
                    <PanelLeftClose className="w-4 h-4" />
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center shrink-0"
                title={`${user?.first_name || ''} ${user?.last_name || ''}\n${user?.email || ''}\n${versionLabel}`}>
                <span className="text-xs font-medium">{initials}</span>
              </div>
              <ThemeMenu rail />
              <button type="button" onClick={handleLogout}
                className="flex items-center justify-center min-w-[44px] min-h-[44px] hover:bg-slate-800 rounded-md transition-colors text-slate-400 hover:text-slate-200"
                data-testid="logout-btn" title="Sign out" aria-label="Sign out">
                <LogOut className="w-4 h-4" />
              </button>
              <button type="button" onClick={onToggleMode}
                className="flex items-center justify-center min-w-[44px] min-h-[44px] hover:bg-slate-800 rounded-md transition-colors text-slate-400 hover:text-slate-200"
                data-testid="sidebar-collapse-btn" title="Expand sidebar" aria-label="Expand sidebar">
                <PanelLeftOpen className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
